import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, of, takeUntil } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import {
  EntitySelectComponent,
  EntitySelectPage
} from '../../../shared/components/entity-select/entity-select.component';
import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { UserApiService } from '../../users/services/user-api.service';
import { CrmUserResponse } from '../../users/models/user.model';
import { TicketsApiService } from '../services/tickets-api.service';
import {
  Ticket,
  TicketCommentResponse,
  TicketSplitPart,
  TicketSubTicketSummary
} from '../models/tickets.model';
import {
  TicketCategory,
  TicketPriority,
  TicketRootCause,
  TicketStatus
} from '../models/tickets-config.model';

export type TicketActionType =
  | 'reopen'
  | 'escalate'
  | 'resolve'
  | 'sub-ticket'
  | 'merge'
  | 'split';

interface SplitPartForm {
  subject: string;
  description: string;
  commentIds: number[];
}

/**
 * Modal único multi-acción. Renderiza el formulario adecuado según `action`.
 * El parent (detail page) controla la apertura mediante `action: TicketActionType | null`.
 *
 * Outputs:
 *  - `closed` cuando el usuario cancela / cierra.
 *  - `saved` cuando la acción se completó OK. Para Reopen/Escalate/Resolve emite el ticket
 *    actualizado; para Sub-ticket emite el sub-ticket creado; para Merge emite el primary
 *    actualizado; para Split emite null (el parent debe recargar).
 */
@Component({
  selector: 'app-tickets-actions-modals',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent, EntitySelectComponent],
  templateUrl: './tickets-actions-modals.component.html'
})
export class TicketsActionsModalsComponent implements OnChanges {
  /** EntitySelect usado para @mención en las notas del sub-ticket (se limpia tras insertar). */
  @ViewChild('mentionSelect') mentionSelect?: EntitySelectComponent<CrmUserResponse>;
  /** EntitySelect del buscador de tickets a fusionar (se limpia tras cada selección). */
  @ViewChild('mergeSelect') mergeSelect?: EntitySelectComponent<Ticket>;
  @Input() ticket: Ticket | null = null;
  @Input() action: TicketActionType | null = null;

  // Catálogos para selectores
  @Input() statuses: TicketStatus[] = [];
  @Input() priorities: TicketPriority[] = [];
  @Input() categories: TicketCategory[] = [];
  @Input() rootCauses: TicketRootCause[] = [];

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Ticket | null>();

  // ===== Reopen =====
  reopenReason = '';
  reopenIdStatus: number | null = null;

  // ===== Escalate =====
  escalateIdUserTo: number | null = null;
  escalateReason = '';

  // ===== Resolve =====
  resolveSolution = '';
  resolveIdRootCause: number | null = null;
  resolveIdStatus: number | null = null;

  // ===== Sub-ticket =====
  subTicketSubject = '';
  subTicketDescription = '';
  subTicketIdPriority: number | null = null;
  subTicketIdCategory: number | null = null;
  subTicketIntIdUserOwner: number | null = null;
  subTicketNotes = '';

  // ===== Merge =====
  /** Tickets seleccionados para fusionar (chips) — INC 38. */
  mergeSelected: { id: number; ticketNumber: string; subject: string }[] = [];
  mergeReason = '';
  /**
   * Inc 53: cuando el ticket actual es PADRE (tiene subtickets), la fusión sólo
   * ofrece sus subtickets. Si no tiene subtickets, se cae a la búsqueda global (INC 38).
   */
  parentSubTickets: TicketSubTicketSummary[] = [];
  loadingSubTickets = false;
  /** true => mostrar lista de subtickets del padre en vez del buscador global. */
  mergeUseSubTickets = false;
  /**
   * Inc 53: true cuando `GET /tickets/{id}/sub-tickets` falló. NO se cae a la búsqueda
   * global: no sabemos si el ticket es padre y ofrecer todos los tickets del sistema
   * habilitaría una fusión indebida. Se muestra el error y se ofrece reintentar.
   */
  subTicketsLoadError = false;

  // ===== Split =====
  splitParts: SplitPartForm[] = [];
  ticketComments: TicketCommentResponse[] = [];
  loadingComments = false;

  // ===== Estado común =====
  saving = false;
  error = '';

  private readonly destroy$ = new Subject<void>();
  /** Cache de usuarios activos del negocio (el endpoint no busca por texto → filtramos client-side). */
  private usersCache: CrmUserResponse[] | null = null;

  constructor(
    private ticketsApi: TicketsApiService,
    private toast: ToastService,
    private userApi: UserApiService,
    private auth: AuthService
  ) {}

  private get businessId(): number {
    return this.auth.getCurrentUser()?.businessId || 1;
  }

  // ===== EntitySelect: usuarios (arrow fns para conservar `this`) =====
  userSearchFn = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    const build = (list: CrmUserResponse[]): EntitySelectPage<CrmUserResponse> => {
      const t = term.trim().toLowerCase();
      const items = t
        ? list.filter(
            (u) =>
              `${u.fullname} ${u.lastname}`.toLowerCase().includes(t) ||
              (u.mail || '').toLowerCase().includes(t)
          )
        : list;
      return { items, totalPages: 1 };
    };
    if (this.usersCache) {
      return of(build(this.usersCache));
    }
    return this.userApi.listActiveByBusiness(this.businessId).pipe(
      map((list) => {
        this.usersCache = list;
        return build(list);
      })
    );
  };

  userLabelFn = (u: CrmUserResponse): string => `${u.fullname} ${u.lastname}`.trim();
  userSublabelFn = (u: CrmUserResponse): string => u.mail || '';
  userIdFn = (u: CrmUserResponse): number => u.id;

  // ===== EntitySelect: tickets a fusionar (INC 38) =====
  mergeSearchFn = (term: string): Observable<EntitySelectPage<Ticket>> => {
    const q = (term || '').trim();
    if (!q) return of({ items: [], totalPages: 0 });
    return this.ticketsApi.quickSearchTickets(q).pipe(
      map((list) => ({
        // Excluir el ticket principal y los ya seleccionados.
        items: list.filter(
          (t) => t.id !== this.ticket?.id && !this.mergeSelected.some((s) => s.id === t.id)
        ),
        totalPages: 1
      })),
      catchError(() => of({ items: [] as Ticket[], totalPages: 0 }))
    );
  };

  mergeTicketLabelFn = (t: Ticket): string => t.subject;
  mergeTicketSublabelFn = (t: Ticket): string => t.ticketNumber;
  mergeTicketIdFn = (t: Ticket): number => t.id;

  addMergeTicket(t: Ticket | null): void {
    if (!t || t.id === this.ticket?.id) return;
    if (!this.mergeSelected.some((s) => s.id === t.id)) {
      this.mergeSelected.push({ id: t.id, ticketNumber: t.ticketNumber, subject: t.subject });
    }
    // Limpiar el buscador para permitir elegir otro ticket.
    this.mergeSelect?.clear();
  }

  removeMergeTicket(id: number): void {
    this.mergeSelected = this.mergeSelected.filter((s) => s.id !== id);
  }

  // ===== Inc 53: fusión restringida a los subtickets del padre =====
  loadParentSubTickets(): void {
    if (!this.ticket?.id) return;
    this.loadingSubTickets = true;
    this.parentSubTickets = [];
    this.mergeUseSubTickets = false;
    this.subTicketsLoadError = false;
    this.error = '';
    this.ticketsApi
      .getSubTickets(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.parentSubTickets = list ?? [];
          // Sólo se restringe a subtickets cuando el ticket es realmente un padre.
          // Lista vacía => no es padre => búsqueda global (INC 38), sin error.
          this.mergeUseSubTickets = this.parentSubTickets.length > 0;
          this.loadingSubTickets = false;
        },
        error: (err) => {
          // Inc 53: NO caer a la búsqueda global — sin la respuesta no sabemos si el
          // ticket es padre, y ofrecer todos los tickets permitiría una fusión indebida.
          this.parentSubTickets = [];
          this.mergeUseSubTickets = false;
          this.subTicketsLoadError = true;
          this.loadingSubTickets = false;
          this.handleError(err, 'No se pudieron cargar los subtickets de este ticket');
        }
      });
  }

  isSubTicketSelected(id: number): boolean {
    return this.mergeSelected.some((s) => s.id === id);
  }

  toggleSubTicket(sub: TicketSubTicketSummary): void {
    if (this.isSubTicketSelected(sub.id)) {
      this.mergeSelected = this.mergeSelected.filter((s) => s.id !== sub.id);
    } else {
      this.mergeSelected.push({
        id: sub.id,
        ticketNumber: sub.ticketNumber,
        subject: sub.subject
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['action']) {
      const next = changes['action'].currentValue as TicketActionType | null;
      if (next) {
        this.resetForms();
        if (next === 'split') {
          this.splitParts = [
            { subject: '', description: '', commentIds: [] },
            { subject: '', description: '', commentIds: [] }
          ];
          this.loadComments();
        } else if (next === 'merge') {
          this.loadParentSubTickets();
        }
      }
    }
  }

  get isOpen(): boolean {
    return this.action != null;
  }

  get titulo(): string {
    switch (this.action) {
      case 'reopen':
        return 'Reabrir ticket';
      case 'escalate':
        return 'Escalar ticket';
      case 'resolve':
        return 'Resolver ticket';
      case 'sub-ticket':
        return 'Crear sub-ticket';
      case 'merge':
        return 'Fusionar tickets';
      case 'split':
        return 'Dividir ticket';
      default:
        return '';
    }
  }

  private resetForms(): void {
    this.error = '';
    this.saving = false;
    this.reopenReason = '';
    this.reopenIdStatus = null;
    this.escalateIdUserTo = null;
    this.escalateReason = '';
    this.resolveSolution = '';
    this.resolveIdRootCause = null;
    this.resolveIdStatus = null;
    this.subTicketSubject = '';
    this.subTicketDescription = '';
    this.subTicketIdPriority = null;
    this.subTicketIdCategory = null;
    this.subTicketIntIdUserOwner = null;
    this.subTicketNotes = '';
    this.mergeSelected = [];
    this.mergeReason = '';
    this.parentSubTickets = [];
    this.loadingSubTickets = false;
    this.mergeUseSubTickets = false;
    this.subTicketsLoadError = false;
    this.splitParts = [];
    this.ticketComments = [];
    this.loadingComments = false;
  }

  cerrar(): void {
    this.closed.emit();
  }

  // ===== Actions =====
  ejecutarReopen(): void {
    if (!this.ticket?.id) return;
    this.saving = true;
    this.error = '';
    this.ticketsApi
      .reopenTicket(this.ticket.id, {
        reason: this.reopenReason || undefined,
        idTicketStatus: this.reopenIdStatus ?? undefined
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.saving = false;
          this.toast.success('Ticket reabierto');
          this.saved.emit(updated);
        },
        error: (err) => this.handleError(err, 'Error al reabrir el ticket')
      });
  }

  ejecutarEscalate(): void {
    if (!this.ticket?.id) return;
    if (this.escalateIdUserTo == null) {
      this.error = 'Seleccione un usuario destino';
      return;
    }
    if (!this.escalateReason.trim()) {
      this.error = 'La razón es obligatoria';
      return;
    }
    this.saving = true;
    this.error = '';
    this.ticketsApi
      .escalateTicket(this.ticket.id, {
        idUserTo: this.escalateIdUserTo,
        reason: this.escalateReason.trim()
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast.success('Ticket escalado');
          // El endpoint devuelve TicketEscalationEventResponse, no el ticket.
          // Emitimos null para que el parent recargue el ticket.
          this.saved.emit(null);
        },
        error: (err) => this.handleError(err, 'Error al escalar el ticket')
      });
  }

  ejecutarResolve(): void {
    if (!this.ticket?.id) return;
    if (!this.resolveSolution.trim()) {
      this.error = 'La descripción de la solución es obligatoria';
      return;
    }
    if (this.resolveIdRootCause == null) {
      this.error = 'La causa raíz es obligatoria';
      return;
    }
    this.saving = true;
    this.error = '';
    this.ticketsApi
      .resolveTicket(this.ticket.id, {
        solutionText: this.resolveSolution.trim(),
        idRootCause: this.resolveIdRootCause,
        idTicketStatus: this.resolveIdStatus ?? undefined
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast.success('Ticket resuelto');
          // El endpoint devuelve TicketResolutionResponse — emitimos null para forzar recarga del ticket.
          this.saved.emit(null);
        },
        error: (err) => this.handleError(err, 'Error al resolver el ticket')
      });
  }

  ejecutarSubTicket(): void {
    if (!this.ticket?.id) return;
    if (!this.subTicketSubject.trim()) {
      this.error = 'El asunto es obligatorio';
      return;
    }
    this.saving = true;
    this.error = '';
    this.ticketsApi
      .createSubTicket(this.ticket.id, {
        subject: this.subTicketSubject.trim(),
        description: this.subTicketDescription || undefined,
        idTicketPriority: this.subTicketIdPriority ?? undefined,
        idTicketCategory: this.subTicketIdCategory ?? undefined,
        intIdUserOwner: this.subTicketIntIdUserOwner ?? undefined,
        notes: this.subTicketNotes || undefined
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sub) => {
          this.saving = false;
          // Inc 74: el usuario se queda en el ticket padre → el toast indica el
          // número del sub-ticket creado para que sepa cuál se generó.
          this.toast.success({
            title: 'Sub-ticket creado',
            message: `Se creó el sub-ticket ${sub.ticketNumber}.`
          });
          this.saved.emit(sub);
        },
        error: (err) => this.handleError(err, 'Error al crear el sub-ticket')
      });
  }

  ejecutarMerge(): void {
    if (!this.ticket?.id) return;
    const mergedIds = this.mergeSelected.map((s) => s.id);
    if (mergedIds.length === 0) {
      this.error = 'Seleccione al menos un ticket a fusionar';
      return;
    }
    this.saving = true;
    this.error = '';
    this.ticketsApi
      .mergeTickets({
        primaryTicketId: this.ticket.id,
        mergedTicketIds: mergedIds,
        reason: this.mergeReason || undefined
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (primary) => {
          this.saving = false;
          this.toast.success('Tickets fusionados');
          this.saved.emit(primary);
        },
        error: (err) => this.handleError(err, 'Error al fusionar tickets')
      });
  }

  ejecutarSplit(): void {
    if (!this.ticket?.id) return;
    const validParts: TicketSplitPart[] = this.splitParts
      .filter((p) => p.subject.trim())
      .map((p) => {
        const part: TicketSplitPart = { subject: p.subject.trim() };
        if (p.description) part.description = p.description;
        if (p.commentIds.length) part.commentIds = [...p.commentIds];
        return part;
      });
    if (validParts.length < 2) {
      this.error = 'Se requieren al menos 2 partes con asunto';
      return;
    }
    this.saving = true;
    this.error = '';
    this.ticketsApi
      .splitTicket(this.ticket.id, { parts: validParts })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.toast.success('Ticket dividido');
          this.saved.emit(null);
        },
        error: (err) => this.handleError(err, 'Error al dividir el ticket')
      });
  }

  // ===== Helpers Split =====
  agregarParte(): void {
    this.splitParts.push({ subject: '', description: '', commentIds: [] });
  }

  removerParte(index: number): void {
    if (this.splitParts.length <= 2) return;
    this.splitParts.splice(index, 1);
  }

  private loadComments(): void {
    if (!this.ticket?.id) return;
    this.loadingComments = true;
    this.ticketComments = [];
    this.ticketsApi
      .getComments(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.ticketComments = list;
          this.loadingComments = false;
        },
        error: () => {
          this.loadingComments = false;
        }
      });
  }

  isCommentSelected(part: SplitPartForm, commentId: number): boolean {
    return part.commentIds.includes(commentId);
  }

  toggleComment(part: SplitPartForm, commentId: number): void {
    const i = part.commentIds.indexOf(commentId);
    if (i >= 0) {
      part.commentIds.splice(i, 1);
    } else {
      part.commentIds.push(commentId);
    }
  }

  // ===== Helper Sub-ticket: @mención textual en las notas internas =====
  insertMention(user: CrmUserResponse | null): void {
    if (!user) return;
    const name = this.userLabelFn(user);
    const sep = this.subTicketNotes && !/\s$/.test(this.subTicketNotes) ? ' ' : '';
    this.subTicketNotes = `${this.subTicketNotes}${sep}@${name} `;
    // Limpiar el selector para permitir insertar otra mención.
    this.mentionSelect?.clear();
  }

  // ===== Util =====
  private handleError(err: unknown, fallbackMsg: string): void {
    this.saving = false;
    const msg =
      (err as { error?: { message?: string } })?.error?.message || fallbackMsg;
    this.error = msg;
    this.toast.error({ title: 'Error', message: msg });
  }
}
